/**
 * A one-line pub/sub fired after every SUCCESSFUL credit write.
 *
 * Why this exists: the credit ledger lives in ONE shared store
 * (customerCreditsStore) read by every display surface (Credits tab, dashboard
 * tile, draft-row chips, invoice sidebar), kept live by useCreditsLive — a
 * realtime ping from the DB + THIS bus (for this tab's own writes, instant) +
 * a resume check + a settle refetch. Billing has NO realtime on the ledger
 * itself (CLAUDE.md data-freshness doctrine), so the bus is how a write made
 * in this tab reaches the store without waiting on the ping. Without it,
 * granting a credit left the dashboard tile on the OLD total and applying
 * credit from the invoice sidebar left the draft-row chip advertising money
 * already committed (found in code review, 2026-08-08).
 *
 * The bump lives in customerCreditService's write wrappers — the ONE
 * chokepoint all five RPCs pass through — so a future credit write cannot
 * forget to fire it. This is a REFRESH HINT, never a data channel: listeners
 * re-read from the DB, they are not handed a payload to trust.
 *
 * Deliberately module-scoped rather than a React context: the listeners live
 * in hooks mounted under different, sometimes-unmounted subtrees (the
 * dashboard and the invoice overlay are never mounted together on mobile), so
 * a provider would have to sit above both and thread through everything.
 *
 * `detail` (2026-08-12) is an OPTIONAL escape hatch on top of the hint: a
 * write that already knows exactly what changed (e.g. applyCustomerCredit-
 * ToInvoice's own RPC response) can hand listeners a server-authoritative
 * patch so a dashboard's in-memory `invoices` row can be corrected same-tick
 * instead of waiting on a full reconcile. See applyCreditInvoicePatch.js. A
 * bare `bumpCreditsMutation()` still fires every subscriber exactly as
 * before — `detail` is `undefined`, which a no-arg listener (the two
 * pre-existing `() => {...}` subscribers) simply ignores.
 *
 * `invoice_patch` now has TWO producers (RD lifecycle-truth, 2026-09):
 * the credit apply/release RPCs (customerCreditService), and
 * addJobsToInvoiceDraft (a draft-contents write — adding unbilled visits to
 * an existing draft), both handing the row their own write actually
 * returned. useCreditsLive treats ANY bump as a re-read trigger for the shared
 * store, so a draft-contents add costs one extra (no-op) ledger reload
 * (throttled, coalesced). Accepted rather than adding a second bus.
 */
let listeners = new Set();

/**
 * Fire after a successful grant / apply / release / remove / transfer.
 * @param {object} [detail] optional payload for a listener that can act on it
 *   directly (e.g. {kind:'invoice_patch', invoiceId, patch}); every other
 *   listener should keep treating this as a pure re-read trigger.
 */
export function bumpCreditsMutation(detail) {
  // Snapshot before iterating: a listener that unsubscribes during its own
  // callback (a hook unmounting on the state update it just triggered) would
  // otherwise mutate the Set mid-iteration.
  [...listeners].forEach((fn) => {
    try {
      fn(detail);
    } catch (err) {
      // A broken listener must never fail the write that fired it — the
      // mutation already committed server-side by the time we get here.
      console.warn('[creditsMutationSignal] listener threw', err);
    }
  });
}

/** Returns an unsubscribe fn. Safe to call in a useEffect cleanup. */
export function subscribeCreditsMutation(fn) {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** Test-only — the Set is module state and survives across `it()` blocks. */
export function __resetCreditsMutationListenersForTests() {
  listeners = new Set();
}
